<template>
	<Modal width="large" @close="emitCloseModal" @submit="saveCleaner" :texts="{ headerPrimary: modalTitle }">
		<section class="form-container">
			<!-- All Fields Section -->
			<div class="form-section">
				<div class="section-header">
					<span class="section-title">{{ $t('cleaner') || 'Cleaner' }}</span>
				</div>
				<div class="section-content grid-layout">
					<template v-for="(input, idx) in modalInputs">
						<div class="form-field" :key="input.keyInCleaner + idx">
							<label class="field-label">
								<span>{{ $t(input.label) }}</span>
								<span v-if="isRequiredField(input.keyInCleaner)" class="required-indicator">*</span>
							</label>
							<Input
								class="form-input"
								dir="auto"
								@click.right.prevent
								:class="input.keyInCleaner + '-input'"
								:placeholder="$t('insert_text')"
								type="text"
								v-model.trim="cleaner[input.keyInCleaner]"
								:flash="isErrorShown(input.label) || isErrorShown(input.label + '_length')"
							/>
							<ErrorMsg
								v-if="input.keyInCleaner === 'username'"
								:isShown="isErrorShown('username_length')"
								:text="$t('username_must_be_at_least_5_characters')"
							/>
							<ErrorMsg
								v-if="input.keyInCleaner === 'username'"
								:isShown="isErrorShown('username') && !isErrorShown('username_length')"
								:text="$t('invalid_username')"
							/>
							<ErrorMsg
								v-if="input.keyInCleaner === 'phone'"
								:isShown="isErrorShown('phone')"
								:text="$t('invalid_phone')"
							/>
						</div>
					</template>

					<div class="form-field">
						<label class="field-label">
							<span>{{ $t('supervisor') }}</span>
							<span class="required-indicator">*</span>
						</label>
						<Select class="form-select" v-model="cleaner.userId">
							<Option
								v-for="(supervisor, idx) in supervisors"
								:key="supervisor.id + idx"
								:label="`${supervisor.firstName} ${supervisor.lastName}`"
								:value="supervisor.id"
							/>
						</Select>
						<ErrorMsg :isShown="isErrorShown('supervisor')" :text="`${$t('please_select')} ${$t('supervisor')}`" />
					</div>

					<div class="form-field">
						<label class="field-label">
							<span>{{ $t('role') }}</span>
						</label>
						<Select class="form-select" v-model="cleaner.role">
							<Option
								v-for="(role, idx) in cleanerRoles"
								:key="role + idx"
								:label="$t(role)"
								:value="role"
							/>
						</Select>
					</div>

					<div class="form-field">
						<label class="field-label">
							<span>{{ $t('rooms') }}</span>
						</label>
						<Select class="form-select" :value="cleaner.roomIds" :multiple="true" @input="handleRoomSelection">
							<SearchBar v-model="roomSearch" />
							<Option :key="'select-all'" :label="$t('select_all')" :value="selectAllValue" />
							<Option
								v-for="(room, idx) in filteredRooms"
								:key="room.id + idx"
								:label="room.display_name"
								:value="room.id"
							/>
						</Select>
					</div>

					<div class="form-field">
						<label class="field-label">
							<span>{{ $t('working_areas') }}</span>
						</label>
						<Select class="form-select" v-model="cleaner.workingAreaIds" :multiple="true" :disabled="!hasCurrentSiteRooms">
							<Option
								v-for="(workingArea, idx) in workingAreas"
								:key="workingArea.id + idx"
								:label="workingArea.area_name"
								:value="workingArea.id"
							/>
						</Select>
					</div>

					<div class="form-field">
						<label class="field-label">
							<span>{{ $t('shifts') }}</span>
							<span class="required-indicator">*</span>
						</label>
						<Select class="form-select" v-model="cleaner.shiftIds" :multiple="true" :disabled="!hasCurrentSiteRooms">
							<Option
								v-for="(shift, idx) in shifts"
								:key="shift.id + idx"
								:label="shift.shift_name"
								:value="shift.id"
							/>
						</Select>
						<ErrorMsg :isShown="isErrorShown('shifts')" :text="`${$t('please_select')} ${$t('shifts')}`" />
					</div>

					<div class="form-field">
						<label class="field-label">
							<span>{{ $t('app_pages') }}</span>
							<span class="required-indicator">*</span>
						</label>
						<Select class="form-select" v-model="cleaner.appPagesIds" :multiple="true">
							<Option
								v-for="(page, idx) in appPages"
								:key="page.id + idx"
								:label="$t(page.name)"
								:value="page.id"
							/>
						</Select>
						<ErrorMsg :isShown="isErrorShown('app_pages')" :text="`${$t('please_select')} ${$t('app_pages')}`" />
					</div>

					<div class="form-field">
						<div class="status-toggle">
							<label class="toggle-label">
								<span>{{ $t('active') }}</span>
							</label>
							<Checkbox class="checkbox-container" v-model="cleaner.isActive" />
						</div>
					</div>
				</div>
			</div>

			<!-- Notifications: settings + health side by side -->
			<div v-if="cleaner.id && cleaner.phone" class="notifications-row" :class="{ 'notifications-row--single': !hasAppUser }">
				<div v-if="hasAppUser" class="form-section notifications-section">
					<div class="section-header section-header--clickable" @click="showSettings = !showSettings">
						<span class="section-title">{{ $t('notifications') }} {{ $t('settings') }}</span>
						<span class="settings-toggle-icon">{{ showSettings ? '▼' : '▶' }}</span>
					</div>
					<div v-if="showSettings" class="section-content notifications-content">
						<div class="settings-group">
								<div class="settings-group-title">{{ $t('when_to_notify') }}</div>
								<div class="form-field">
									<Select class="form-select" v-model="appSettings.notificationMode">
										<Option :label="$t('always')" value="on" />
										<Option :label="$t('off')" value="off" />
										<Option :label="$t('time_based')" value="time" />
										<Option :label="$t('location_based')" value="location" />
									</Select>
								</div>

								<div v-if="appSettings.notificationMode === 'time'" class="settings-info-box">
									<span>{{ $t('notification_times_info') }}</span>
								</div>

								<div v-if="appSettings.notificationMode === 'location'" class="settings-info-box">
									<span v-if="appSettings.notificationLocation" class="location-details">
										{{ $t('lat') }}: {{ appSettings.notificationLocation.lat?.toFixed(4) }} |
										{{ $t('lon') }}: {{ appSettings.notificationLocation.lon?.toFixed(4) }} |
										{{ $t('radius') }}: {{ appSettings.notificationLocation.radius }}m
									</span>
									<span v-else class="location-placeholder">{{ $t('no_location_set') }}</span>
								</div>
							</div>

							<div v-if="appSettings.notificationMode !== 'off'" class="settings-group">
								<div class="settings-group-title">{{ $t('notification_preferences') }}</div>

								<div class="form-field">
									<label class="field-label">
										<span>{{ $t('notification_sound') }}</span>
									</label>
									<Select class="form-select" v-model="appSettings.selectedNotificationSound">
										<Option :label="$t('no_sound')" value="none" />
										<Option :label="$t('default_sound')" value="assets/sound2.mp3" />
										<Option :label="$t('soft_bell')" value="assets/Soft-bell-notification-tone.mp3" />
										<Option :label="$t('digital_sound')" value="assets/Digital-notification-sound.mp3" />
										<Option :label="$t('mallet_sound')" value="assets/Mallet-notification-sound-effect.mp3" />
										<Option :label="$t('phone_sound')" value="assets/Phone-notification-sound-effect.mp3" />
										<Option :label="$t('text_sound')" value="assets/Text-notification-sound-effect.mp3" />
									</Select>
								</div>

								<div class="form-field">
									<label class="field-label">
										<span>{{ $t('snooze_interval') }}</span>
									</label>
									<Select class="form-select" v-model.number="appSettings.snoozeInterval">
										<Option :label="$t('no_snooze')" :value="0" />
										<Option :label="`5 ${$t('minutes')}`" :value="5" />
										<Option :label="`10 ${$t('minutes')}`" :value="10" />
										<Option :label="`15 ${$t('minutes')}`" :value="15" />
									</Select>
								</div>

								<div v-if="isAndroidAppUser" class="status-toggle">
									<label class="toggle-label">
										<span>{{ $t('vocal_notifications') }}</span>
									</label>
									<Checkbox class="checkbox-container" v-model="appSettings.isVocalNotificationOn" />
								</div>
							</div>
					</div>
				</div>

				<div class="form-section notification-health-section">
					<div class="section-header section-header--clickable" @click="showHealth = !showHealth">
						<div class="health-header-left">
							<span class="section-title">{{ $t('notification_health') }}</span>
							<span
								v-if="notificationHealth && !isLoadingHealth"
								class="health-status-pill"
								:class="healthStatus.className"
							>
								{{ $t(healthStatus.labelKey) }}
							</span>
						</div>
						<div class="health-header-actions">
							<button
								v-if="showHealth"
								type="button"
								class="health-refresh-btn"
								:disabled="isLoadingHealth"
								@click.stop="loadNotificationHealth"
							>
								{{ $t('notification_health_refresh') }}
							</button>
							<span class="settings-toggle-icon">{{ showHealth ? '▼' : '▶' }}</span>
						</div>
					</div>
					<div v-if="showHealth" class="section-content notifications-content">
						<div v-if="isLoadingHealth" class="panel-loading">{{ $t('notification_health_loading') }}</div>
						<template v-else-if="notificationHealth">
							<div class="health-summary" :class="healthStatus.className">
								<strong>{{ $t('notification_health_summary') }}:</strong>
								<span>{{ healthSummaryText }}</span>
							</div>
							<div v-if="notificationHealth.appUser" class="health-meta">
								<span>{{ $t('device_type') }}: {{ notificationHealth.appUser.device || '-' }}</span>
								<span>{{ $t('notification_health_app_version') }}: {{ notificationHealth.appUser.version || '-' }}</span>
								<span v-if="notificationHealth.lastPushDeliveredAt">
									{{ $t('notification_health_last_delivery') }}: {{ healthLastDeliveryText }}
								</span>
							</div>
							<ul class="health-checks">
								<li
									v-for="check in notificationHealth.checks"
									:key="check.id"
									class="health-check-item"
									:class="{ 'is-ok': check.ok, 'is-fail': !check.ok }"
								>
									<div class="health-check-title">
										<span class="health-check-icon">{{ check.ok ? '✓' : '✕' }}</span>
										<span>{{ healthCheckLabel(check) }}</span>
									</div>
									<div v-if="check.detailKey" class="health-check-detail">{{ healthCheckDetail(check) }}</div>
									<div v-if="check.fixKey" class="health-check-fix">
										<strong>{{ $t('notification_health_fix') }}:</strong> {{ healthCheckFix(check) }}
									</div>
								</li>
							</ul>
						</template>
						<div v-else class="panel-loading">{{ $t('notification_health_unavailable') }}</div>
					</div>
				</div>
			</div>
		</section>
	</Modal>
</template>

<script>
import Input from '@/modules/common/components/Input'
import Modal from '@/modules/common/components/Modal'
import Option from '@/modules/common/components/Option'
import Select from '@/modules/common/components/Select'
import ErrorMsg from '@/modules/common/components/ErrorMsg'
import Checkbox from '@/modules/common/components/Checkbox'
import SearchBar from '@/modules/common/components/SearchBar'
import { isUsernameValid, isPhoneValid, getAppUserSettings, updateAppUserSettings, getContactNotificationHealth } from '@/modules/control-panel/services/contactService'
import {
	formatHealthRelativeTime,
	resolveHealthDetailParams,
	healthCheckLabelKey,
} from '@/modules/users/notificationHealthI18n'

export default {
	name: 'CleanerModal',
	components: { Input, Modal, ErrorMsg, Option, Select, Checkbox, SearchBar },
	props: {
		cleanerProp: { type: Object, required: true },
		languages: { type: Array, required: true },
		supervisors: { type: Array, required: true },
		workingAreas: { type: Array, required: true },
		rooms: { type: Array, required: true },
		shifts: { type: Array, required: true },
		appPages: { type: Array, required: true }
	},
	data() {
		const cleaner = JSON.parse(JSON.stringify(this.cleanerProp))
		const selectAllValue = 'SELECT_ALL_ROOMS'
		
		// Ensure userId is set (handle both userId and user_id)
		if (!cleaner.userId && cleaner.user_id) {
			cleaner.userId = cleaner.user_id
		}
		
		// Ensure workingAreaIds is an array (handle both workingAreaIds and working_area_id)
		if (cleaner.working_area_id && !cleaner.workingAreaIds) {
			cleaner.workingAreaIds = Array.isArray(cleaner.working_area_id) ? cleaner.working_area_id : []
		}
		if (!cleaner.workingAreaIds) {
			cleaner.workingAreaIds = []
		}
		// Ensure it's an array (not a string)
		if (typeof cleaner.workingAreaIds === 'string') {
			try {
				cleaner.workingAreaIds = JSON.parse(cleaner.workingAreaIds)
			} catch (e) {
				cleaner.workingAreaIds = []
			}
		}
		
		// Ensure roomIds is an array
		if (!cleaner.roomIds) {
			cleaner.roomIds = []
		}
		if (typeof cleaner.roomIds === 'string') {
			try {
				cleaner.roomIds = JSON.parse(cleaner.roomIds)
			} catch (e) {
				cleaner.roomIds = []
			}
		}
		
		// Ensure shiftIds is an array
		if (!cleaner.shiftIds) {
			cleaner.shiftIds = []
		}
		if (typeof cleaner.shiftIds === 'string') {
			try {
				cleaner.shiftIds = JSON.parse(cleaner.shiftIds)
			} catch (e) {
				cleaner.shiftIds = []
			}
		}
		
		// Ensure appPagesIds is an array
		if (!cleaner.appPagesIds) {
			cleaner.appPagesIds = []
		}
		if (typeof cleaner.appPagesIds === 'string') {
			try {
				cleaner.appPagesIds = JSON.parse(cleaner.appPagesIds)
			} catch (e) {
				cleaner.appPagesIds = []
			}
		}
		
		// Ensure isActive is boolean
		if (typeof cleaner.isActive !== 'boolean') {
			cleaner.isActive = Boolean(cleaner.isActive)
		}
		
		// If all rooms from this site are already selected, add selectAllValue to roomIds
		// (cleaner may also have rooms from other sites, so don't compare total length)
		if (cleaner.roomIds && Array.isArray(cleaner.roomIds) && this.rooms.length > 0) {
			const allSiteRoomsSelected = this.rooms.every(room => cleaner.roomIds.includes(room.id))
			if (allSiteRoomsSelected && !cleaner.roomIds.includes(selectAllValue)) {
				cleaner.roomIds = [...cleaner.roomIds, selectAllValue]
			}
		}
		
		// Multi-site support: snapshot original IDs BEFORE v-model components can strip non-matching values
		// These frozen copies ensure we always know what other sites' data looked like at load time
		const currentSiteRoomIdSet = new Set(this.rooms.map(room => room.id))
		const originalRoomIds = [...(cleaner.roomIds || [])].filter(id => id !== selectAllValue)
		const otherSiteRoomIds = originalRoomIds.filter(id => !currentSiteRoomIdSet.has(id))
		
		const currentSiteWorkingAreaIds = new Set(this.workingAreas.map(wa => wa.id))
		const otherSiteWorkingAreaIds = (cleaner.workingAreaIds || []).filter(id => !currentSiteWorkingAreaIds.has(id))
		
		const currentSiteShiftIds = new Set(this.shifts.map(s => s.id))
		const otherSiteShiftIds = (cleaner.shiftIds || []).filter(id => !currentSiteShiftIds.has(id))
		
		if (!cleaner.role) cleaner.role = ''
		return {
			errors: [],
			roomSearch: '',
			cleanerRoles: [
				'restroom_cleaner',
				'general_cleaner',
				'day_porter',
				'office_cleaner',
				'public_area_cleaner',
				'other'
			],
			cleaner,
			modalInputs: [
				{
					label: 'phone',
					keyInCleaner: 'phone'
				},
				{
					label: 'username',
					keyInCleaner: 'username'
				}
			],
			originalFields: {
				username: this.cleanerProp.username || '',
				phone: this.cleanerProp.phone || ''
			},
			selectAllValue,
			isHandlingRoomSelection: false,
			otherSiteRoomIds,
			otherSiteWorkingAreaIds,
			otherSiteShiftIds,
			showSettings: false,
			appSettings: {
				notificationMode: 'on',
				notificationTimes: [[], [], [], [], [], [], []],
				notificationLocation: null,
				managerAlertDelayTime: 0,
				snoozeInterval: 0,
				isVocalNotificationOn: true,
				selectedNotificationSound: 'assets/Text-notification-sound-effect.mp3'
			},
			isLoadingSettings: false,
			hasAppUser: false,
			showHealth: true,
			notificationHealth: null,
			isLoadingHealth: false
		}
	},
	async mounted() {
		if (this.cleaner.id && this.cleaner.phone) {
			await Promise.all([this.loadAppSettings(), this.loadNotificationHealth()])
		}
	},
	methods: {
		isRequiredField(fieldName) {
			const requiredFields = ['username', 'phone', 'userId', 'shiftIds', 'appPagesIds']
			return requiredFields.includes(fieldName)
		},
		async loadAppSettings() {
			if (!this.cleaner.phone) return
			try {
				this.isLoadingSettings = true
				const settings = await getAppUserSettings(this.cleaner.phone)
				if (settings) {
					this.hasAppUser = true
					this.appSettings = {
						notificationMode: settings.notificationMode || 'on',
						notificationTimes: settings.notificationTimes || [[], [], [], [], [], [], []],
						notificationLocation: settings.notificationLocation || null,
						managerAlertDelayTime: settings.managerAlertDelayTime || 0,
						snoozeInterval: settings.snoozeInterval || 0,
						isVocalNotificationOn: settings.isVocalNotificationOn !== undefined ? settings.isVocalNotificationOn : true,
						selectedNotificationSound: settings.selectedNotificationSound || 'assets/Text-notification-sound-effect.mp3'
					}
				}
			} catch (error) {
				// User hasn't logged into the app yet - no settings to show
				this.hasAppUser = false
			} finally {
				this.isLoadingSettings = false
			}
		},
		async loadNotificationHealth() {
			if (!this.cleaner.phone) return
			try {
				this.isLoadingHealth = true
				this.notificationHealth = await getContactNotificationHealth(this.cleaner.phone)
			} catch (error) {
				this.notificationHealth = null
			} finally {
				this.isLoadingHealth = false
			}
		},
		healthCheckLabel(check) {
			return this.$t(healthCheckLabelKey(check.id))
		},
		healthCheckDetail(check) {
			return this.$t(check.detailKey, resolveHealthDetailParams(check.detailParams, this.$t.bind(this)))
		},
		healthCheckFix(check) {
			return this.$t(check.fixKey, check.detailParams || {})
		},
		emitCloseModal() {
			this.$emit('closeModal')
		},
		handleRoomSelection(selectedValues) {
			// Prevent infinite loop - setting roomIds triggers @input again
			if (this.isHandlingRoomSelection) {
				return
			}
			
			if (!Array.isArray(selectedValues)) {
				selectedValues = []
			}
			
			this.isHandlingRoomSelection = true
			
			try {
				// IDs of rooms visible in this site's dropdown
				const currentSiteRoomIds = new Set(this.rooms.map(room => room.id))
				
				// Check if "Select All" was clicked
				const previousRoomIds = this.cleaner.roomIds || []
				const wasSelectAllSelected = previousRoomIds.includes(this.selectAllValue)
				const isSelectAllSelected = selectedValues.indexOf(this.selectAllValue) !== -1
				
				// Get actual room IDs from the selection (excluding selectAllValue)
				const selectedRoomIds = selectedValues.filter(val => val !== this.selectAllValue)
				
				let currentSiteSelections
				
				// If "Select All" was just selected
				if (isSelectAllSelected && !wasSelectAllSelected) {
					currentSiteSelections = [...this.rooms.map(room => room.id), this.selectAllValue]
				}
				// If "Select All" was just deselected
				else if (!isSelectAllSelected && wasSelectAllSelected) {
					currentSiteSelections = []
				}
				// If individual rooms were selected/deselected
				else {
					const thisSiteSelected = selectedRoomIds.filter(id => currentSiteRoomIds.has(id))
					const allRoomsSelected = thisSiteSelected.length === this.rooms.length && this.rooms.length > 0
					
					if (allRoomsSelected) {
						currentSiteSelections = [...thisSiteSelected, this.selectAllValue]
					} else {
						currentSiteSelections = thisSiteSelected
					}
				}
				
				// Merge: frozen other-site room IDs + this site's selections
				const newRoomIds = [...this.otherSiteRoomIds, ...currentSiteSelections]
				
				// Only update if actually changed to avoid unnecessary re-renders
				const currentSet = new Set(previousRoomIds)
				const newSet = new Set(newRoomIds)
				const hasChanged = currentSet.size !== newSet.size || 
					[...newSet].some(val => !currentSet.has(val))
				
				if (hasChanged) {
					this.cleaner.roomIds = newRoomIds
					
					// Cascading clear: if no rooms selected for this site, clear working areas and shifts
					const thisSiteActualRooms = currentSiteSelections.filter(id => id !== this.selectAllValue)
					if (thisSiteActualRooms.length === 0) {
						// Clear only this site's working areas and shifts (keep other-site ones)
						const currentSiteWorkingAreaIds = new Set(this.workingAreas.map(wa => wa.id))
						const currentSiteShiftIds = new Set(this.shifts.map(s => s.id))
						this.cleaner.workingAreaIds = (this.cleaner.workingAreaIds || []).filter(id => !currentSiteWorkingAreaIds.has(id))
						this.cleaner.shiftIds = (this.cleaner.shiftIds || []).filter(id => !currentSiteShiftIds.has(id))
					}
				}
			} catch (error) {
				// Silent catch - room selection error
			}
			
			// Keep the flag set through the next tick to block any
			// re-entrant @input events triggered by the roomIds change
			this.$nextTick(() => {
				this.isHandlingRoomSelection = false
			})
		},
		async checkForm() {
			this.errors = []

			const restrictedUsernames = ['admin', 'viewer', 'installer', 'supervisor']
			const { id, phone, userId: supervisor, username, shiftIds, appPagesIds } = this.cleaner

			const { username: originalUsername, phone: originalPhone } = this.originalFields

			const usernameRegex = /^[\p{L}\p{N}]{5,}$/u;
			const phoneRegex = /^\d+$/

			// Helper to check and add error
			const addErrorIfFalsy = (condition, errorField) => {
				if (!condition) this.addError(errorField)
			}

			// Check username length first, then other username validations
			if (username) {
				if (username.length < 3) {
					this.addError('username_length')
				} else if (restrictedUsernames.includes(username) || !usernameRegex.test(username)) {
					this.addError('username')
				}
			} else {
				this.addError('username')
			}

			addErrorIfFalsy(phone && phoneRegex.test(phone) && phone.length >= 10, 'phone')
			addErrorIfFalsy(supervisor, 'supervisor')
			// Account for shifts from other sites when validating
			const allShiftIds = [...(shiftIds || []), ...this.otherSiteShiftIds]
			addErrorIfFalsy(allShiftIds.length > 0, 'shifts')
			addErrorIfFalsy(appPagesIds && appPagesIds.length > 0, 'app_pages')

			// Only check server-side validations if basic validations pass
			if (!this.isErrorShown('username_length') && !this.isErrorShown('username') && username) {
				const validations = {
					isUsernameValidPrm:
						(!id || username !== originalUsername) ? isUsernameValid(username) : true,
					isPhoneValidPrm: (!id || phone !== originalPhone) && phone ? isPhoneValid(phone) : true
				}

				try {
					const [isUsernameValidRes, isPhoneValidRes] = await Promise.all(Object.values(validations))

					if (!isUsernameValidRes) this.addError('username')
					if (!isPhoneValidRes) this.addError('phone')
				} catch (error) {
					// Handle error
				}
			} else if (phone && !this.isErrorShown('phone')) {
				// Still check phone validation if username validation fails
				const isPhoneValidPrm = (!id || phone !== originalPhone) ? isPhoneValid(phone) : true
				try {
					const isPhoneValidRes = await isPhoneValidPrm
					if (!isPhoneValidRes) this.addError('phone')
				} catch (error) {
					// Handle error
				}
			}
		},
		async saveCleaner() {
			await this.checkForm()
			if (this.errors.length > 0) return
			
			// Get current site's room selections (filter out selectAllValue marker)
			const currentSiteRoomIds = (this.cleaner.roomIds || []).filter(id => id !== this.selectAllValue)
			// Merge: current site rooms + frozen other-site rooms
			// De-duplicate using Set in case there's overlap
			const mergedRoomIds = [...new Set([...currentSiteRoomIds, ...this.otherSiteRoomIds])]
			
			// Merge working areas and shifts from other sites
			const mergedWorkingAreaIds = [...new Set([...(this.cleaner.workingAreaIds || []), ...this.otherSiteWorkingAreaIds])]
			const mergedShiftIds = [...new Set([...(this.cleaner.shiftIds || []), ...this.otherSiteShiftIds])]
			
			const cleanerToSave = {
				...this.cleaner,
				roomIds: mergedRoomIds,
				workingAreaIds: mergedWorkingAreaIds,
				shiftIds: mergedShiftIds
			}
			
			// Save app settings only if the user has logged into the app before
			if (cleanerToSave.phone && this.cleaner.id && this.hasAppUser) {
				try {
					await updateAppUserSettings(cleanerToSave.phone, this.appSettings)
				} catch (error) {
					// Don't block cleaner save if settings save fails
				}
			}
			
			this.$emit('save', cleanerToSave)
		},
		addError(error) {
			this.errors.push(error)
		},
		isErrorShown(error) {
			return this.errors.includes(error)
		}
	},
	computed: {
		modalTitle() {
			return this.cleaner.id ? this.$t('edit_cleaner') : this.$t('new_cleaner')
		},
		filteredRooms() {
			const query = (this.roomSearch || '').trim().toLowerCase()
			if (!query) return this.rooms
			return this.rooms.filter((room) => String(room.display_name || '').toLowerCase().includes(query))
		},
		langFlag() {
			const { lang } = this.cleaner
			return this.languages.find((language) => language.value === lang)?.logo_url
		},
		hasCurrentSiteRooms() {
			const currentSiteRoomIds = new Set(this.rooms.map(room => room.id))
			return (this.cleaner.roomIds || []).some(id => id !== this.selectAllValue && currentSiteRoomIds.has(id))
		},
		isAndroidAppUser() {
			return this.notificationHealth?.appUser?.device === 'android'
		},
		healthStatus() {
			const status = this.notificationHealth?.status || 'unknown'
			const labelMap = {
				healthy: 'notification_health_healthy',
				blocked: 'notification_health_blocked',
				degraded: 'notification_health_degraded',
				no_app_user: 'notification_health_no_app_user',
			}
			return {
				labelKey: labelMap[status] || 'notification_health_unknown',
				className: `health-status-${status}`,
			}
		},
		healthSummaryText() {
			if (!this.notificationHealth?.summaryKey) return ''
			return this.$t(
				this.notificationHealth.summaryKey,
				this.notificationHealth.summaryParams || {},
			)
		},
		healthLastDeliveryText() {
			if (!this.notificationHealth?.lastPushDeliveredAt) return ''
			return formatHealthRelativeTime(
				this.notificationHealth.lastPushDeliveredAt,
				this.$t.bind(this),
			)
		}
	},
	watch: {
		'cleaner.appPagesIds': {
			handler(newVal) {
				if (!newVal || newVal.length === 0) {
					const firstPage = this.appPages[0]
					if (firstPage) {
						this.cleaner.appPagesIds = [firstPage.id]
					}
				}
			},
			immediate: true,
			deep: true
		}
	}
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

.form-container {
	display: flex;
	flex-direction: column;
	gap: 12px;
	padding: 0;
	align-items: stretch;
	width: 100%;
}

.form-section {
	background: #ffffff;
	border-radius: 12px;
	border: 1px solid #e1e8ed;
	overflow: hidden;
	transition: all 0.2s ease;
	
	&:hover {
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
	}
	
	&:first-child {
		flex: 0 0 auto;
		width: 100%;
	}
}

.notifications-row {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 12px;
	width: 100%;
	align-items: start;

	@media (max-width: 1100px) {
		grid-template-columns: 1fr;
	}

	.form-section {
		width: 100%;
		min-width: 0;
	}
}

.notifications-row--single {
	grid-template-columns: 1fr;
}

.panel-loading {
	font-size: 13px;
	color: #6c757d;
}

.section-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 10px 16px;
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	border-bottom: 2px solid #dee2e6;

	&--clickable {
		cursor: pointer;
	}
	
	.section-title {
		font-size: 15px;
		font-weight: 600;
		color: #2c3e50;
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}
}

.section-content {
	padding: 12px 16px 14px;
	display: flex;
	flex-direction: column;
	gap: 10px;
	width: 100%;
	
	&.grid-layout {
		display: grid;
		grid-template-columns: repeat(3, 1fr); // 3 columns for more inputs side by side
		gap: 10px 14px;
		width: 100%;
		
		@media (max-width: 1400px) {
			grid-template-columns: repeat(2, 1fr); // 2 columns on medium screens
		}
		
		@media (max-width: 768px) {
			grid-template-columns: 1fr; // 1 column on small screens
		}
	}
	
	&.notifications-content {
		width: auto;
		max-width: 100%;
	}
}

.form-field {
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 0;
}

.field-label {
	display: flex;
	align-items: center;
	gap: 6px;
	font-size: 13px;
	font-weight: 600;
	color: #34495e;
	margin-bottom: 0;
	
	.required-indicator {
		color: #e74c3c;
		font-weight: bold;
		margin-left: 2px;
	}
}

.form-input,
.form-select {
	width: 100%;
	border: 2px solid #e1e8ed;
	border-radius: 8px;
	padding: 10px 14px;
	font-size: 14px;
	transition: all 0.2s ease;
	background-color: #ffffff;
	
	&:focus {
		outline: none;
		border-color: #3498db;
		box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
	}
	
	&:hover:not(:focus) {
		border-color: #bdc3c7;
	}
}

.form-select {
	cursor: pointer;
	margin: 0 !important;
	padding: 0 10px !important;
	min-height: 40px !important;
}

.md-field.form-select {
	width: 100% !important;
}

.status-toggle {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 12px 16px;
	background-color: #f8f9fa;
	border-radius: 8px;
	border: 2px solid #e1e8ed;
	transition: all 0.2s ease;
	
	&:hover {
		background-color: #f1f3f5;
		border-color: #ced4da;
	}
	
	.toggle-label {
		display: flex;
		align-items: center;
		font-size: 14px;
		font-weight: 500;
		color: #2c3e50;
	}
	
	.checkbox-container {
		height: 20px;
		width: 20px;
		cursor: pointer;
	}
}

.settings-toggle-icon {
	font-size: 12px;
	color: #5a6c7d;
	font-weight: normal;
	margin-left: auto;
}

.settings-group {
	margin-bottom: 25px;
	
	&:last-child {
		margin-bottom: 0;
	}
}

.settings-group-title {
	font-size: 14px;
	font-weight: 600;
	color: #34495e;
	margin-bottom: 12px;
	padding-bottom: 8px;
	border-bottom: 2px solid #e8ecf1;
	text-transform: uppercase;
	letter-spacing: 0.5px;
}

.setting-label {
	display: flex;
	align-items: center;
	margin-bottom: 8px;
	font-size: 13px;
	font-weight: 500;
	color: #2c3e50;
}

.setting-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 12px 0;
	border-bottom: 1px solid #f0f0f0;
	
	&:last-child {
		border-bottom: none;
	}
}

.settings-info-box {
	margin-top: 12px;
	padding: 12px;
	background-color: #f8f9fa;
	border-left: 3px solid #3498db;
	border-radius: 4px;
	font-size: 12px;
	color: #5a6c7d;
	line-height: 1.5;
	
	.location-details {
		font-family: 'Courier New', monospace;
		font-size: 11px;
		color: #2c3e50;
	}
	
	.location-placeholder {
		color: #95a5a6;
		font-style: italic;
	}
}

.input-container {
	margin-bottom: 18px;
	
	&:last-child {
		margin-bottom: 0;
	}
}

.health-header-left {
	display: flex;
	align-items: center;
	gap: 10px;
}

.health-header-actions {
	display: flex;
	align-items: center;
	gap: 10px;
}

.health-status-pill {
	font-size: 11px;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.4px;
	padding: 4px 8px;
	border-radius: 999px;
}

.health-status-healthy {
	background: #d4edda;
	color: #155724;
}

.health-status-blocked,
.health-status-no_app_user {
	background: #f8d7da;
	color: #721c24;
}

.health-status-degraded {
	background: #fff3cd;
	color: #856404;
}

.health-refresh-btn {
	border: 1px solid #ced4da;
	background: #fff;
	border-radius: 6px;
	padding: 4px 10px;
	font-size: 12px;
	cursor: pointer;

	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
}

.panel-loading {
	font-size: 13px;
	color: #6c757d;
}

.health-summary {
	padding: 12px 14px;
	border-radius: 8px;
	font-size: 13px;
	line-height: 1.5;
	margin-bottom: 12px;

	strong {
		display: block;
		margin-bottom: 4px;
	}
}

.health-meta {
	display: flex;
	flex-direction: column;
	gap: 4px;
	font-size: 12px;
	color: #5a6c7d;
	margin-bottom: 12px;
}

.health-checks {
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.health-check-item {
	border: 1px solid #e9ecef;
	border-radius: 8px;
	padding: 10px 12px;
	background: #fff;

	&.is-ok {
		border-left: 4px solid #28a745;
	}

	&.is-fail {
		border-left: 4px solid #dc3545;
	}
}

.health-check-title {
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 13px;
	font-weight: 600;
	color: #2c3e50;
}

.health-check-icon {
	font-weight: 700;
}

.health-check-detail {
	margin-top: 4px;
	font-size: 12px;
	color: #6c757d;
}

.health-check-fix {
	margin-top: 6px;
	font-size: 12px;
	color: #495057;
	line-height: 1.4;
}
</style>

<style lang="scss">
// Override modal width for cleaner modal
// Target modals that contain our form-container
.modal.large:has(.form-container) {
	min-width: 80vw !important;
	max-width: 1400px !important;
	
	.main-content {
		overflow-y: auto !important; // Allow scroll when content exceeds viewport
		max-height: 85vh !important; // Limit height to viewport but allow scroll
	}
}
</style>